import { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/contexts/AuthContext";
import { MessageCircle, Bot, User, Clock, RefreshCw, Loader2, Phone } from "lucide-react";

export default function AdminConversations() {
  const { lang } = useLang();
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/conversations/active");
      setConvos(data.items || []);
      setError("");
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(iv);
  }, []);

  const toggleHandoff = async (cid) => {
    setBusy(cid);
    try {
      await api.post(`/admin/conversations/${cid}/toggle-handoff`);
      await load();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const timeAgo = (iso) => {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return lang === "ar" ? `${Math.floor(diff)} ث` : `${Math.floor(diff)}s`;
    if (diff < 3600) return lang === "ar" ? `${Math.floor(diff / 60)} د` : `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return lang === "ar" ? `${Math.floor(diff / 3600)} س` : `${Math.floor(diff / 3600)}h`;
    return lang === "ar" ? `${Math.floor(diff / 86400)} ي` : `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-conversations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <MessageCircle className="text-emerald-700" size={28} />
            {lang === "ar" ? "المحادثات النشطة" : "Active Conversations"}
          </h1>
          <p className="text-stone-600 mt-1 text-sm">
            {lang === "ar" ? "مراقبة مباشرة — تحديث تلقائي كل 15 ثانية" : "Live monitor — auto-refreshes every 15s"}
          </p>
        </div>
        <Button data-testid="refresh-convos-btn" onClick={load} variant="outline" className="rounded-xl">
          <RefreshCw size={14} className={`me-2 ${loading ? "animate-spin" : ""}`} />
          {lang === "ar" ? "تحديث" : "Refresh"}
        </Button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800">{error}</div>}

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="rounded-2xl border-stone-200"><CardContent className="p-4">
          <p className="text-xs text-stone-500">{lang === "ar" ? "الإجمالي" : "Total"}</p>
          <p className="text-2xl font-bold text-stone-900" data-testid="stat-total">{convos.length}</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-emerald-200 bg-emerald-50/40"><CardContent className="p-4">
          <p className="text-xs text-emerald-700 flex items-center gap-1"><Bot size={12} /> {lang === "ar" ? "بوت" : "Bot"}</p>
          <p className="text-2xl font-bold text-emerald-800" data-testid="stat-bot">{convos.filter(c => !c.handoff).length}</p>
        </CardContent></Card>
        <Card className="rounded-2xl border-amber-200 bg-amber-50/40"><CardContent className="p-4">
          <p className="text-xs text-amber-700 flex items-center gap-1"><User size={12} /> {lang === "ar" ? "إنسان" : "Human"}</p>
          <p className="text-2xl font-bold text-amber-800" data-testid="stat-human">{convos.filter(c => c.handoff).length}</p>
        </CardContent></Card>
      </div>

      {/* Conversation list */}
      <Card className="rounded-3xl border-stone-200">
        <CardContent className="p-0">
          {loading && convos.length === 0 ? (
            <div className="p-12 text-center text-stone-500"><Loader2 className="animate-spin mx-auto mb-2" /> {lang === "ar" ? "تحميل..." : "Loading..."}</div>
          ) : convos.length === 0 ? (
            <div className="p-12 text-center text-stone-500">
              <MessageCircle className="mx-auto mb-3 text-stone-400" size={40} />
              <p className="text-sm">{lang === "ar" ? "لا توجد محادثات نشطة بعد" : "No active conversations yet"}</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {convos.map((c) => (
                <div key={c.conversation_id} data-testid={`convo-row-${c.conversation_id}`} className="p-4 flex items-center gap-4 hover:bg-stone-50 flex-wrap">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.handoff ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {c.handoff ? <User size={18} /> : <Bot size={18} />}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-stone-900">
                        {c.contact_name || (lang === "ar" ? "بدون اسم" : "Unknown")}
                      </p>
                      <Badge variant="outline" className={`text-[10px] ${c.handoff ? "border-amber-300 text-amber-800 bg-amber-50" : "border-emerald-300 text-emerald-800 bg-emerald-50"}`}>
                        {c.handoff ? (lang === "ar" ? "إنسان" : "Human") : (lang === "ar" ? "بوت" : "Bot")}
                      </Badge>
                      {c.consecutive_fallbacks >= 2 && (
                        <Badge className="text-[10px] bg-red-100 text-red-800 hover:bg-red-100">
                          ⚠ {c.consecutive_fallbacks}× {lang === "ar" ? "فشل" : "fallback"}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-stone-500 flex items-center gap-3 mt-1 flex-wrap">
                      {c.wa_id && <span className="flex items-center gap-1" dir="ltr"><Phone size={11} /> {c.wa_id}</span>}
                      <span className="flex items-center gap-1"><MessageCircle size={11} /> {c.message_count}</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> {timeAgo(c.last_reply_at)}</span>
                      <span className="text-stone-400">#{c.conversation_id}</span>
                    </p>
                  </div>
                  <Button
                    data-testid={`toggle-handoff-${c.conversation_id}`}
                    onClick={() => toggleHandoff(c.conversation_id)}
                    disabled={busy === c.conversation_id}
                    variant={c.handoff ? "outline" : "default"}
                    size="sm"
                    className={c.handoff ? "border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-xl" : "bg-amber-600 hover:bg-amber-700 text-white rounded-xl"}
                  >
                    {busy === c.conversation_id ? (
                      <Loader2 size={12} className="animate-spin me-1" />
                    ) : c.handoff ? (
                      <><Bot size={12} className="me-1" /> {lang === "ar" ? "أعد للبوت" : "Give to bot"}</>
                    ) : (
                      <><User size={12} className="me-1" /> {lang === "ar" ? "تولّي يدوي" : "Take over"}</>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
